import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type GoalRecord from 'webapp/models/goal';

export default class GoalSerializer extends ApplicationSerializer {
	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Goal.Raw | RecordData.Goal.Serialized,
		prop: string | undefined,
	) {
		const { setup, ...data } = json;
		const attributes: Partial<RecordAttributes<GoalRecord>> & { id?: string | number } = {
			...data,
		};

		const mainMetricSetup = setup?.mainMetric?.metricSetup;
		if (mainMetricSetup) {
			const normalizedMetricSetup = this.normalizeMetricSetupData(mainMetricSetup)!;
			attributes.mainMetricSetupKey = normalizedMetricSetup.key;
			attributes.mainMetricSetupSpaceAccessId = normalizedMetricSetup.spaceAccessId;
			attributes.mainMetricSetupDimensions = normalizedMetricSetup.dimensions;
			attributes.mainMetricSetupFilters = normalizedMetricSetup.filters || null;
		}

		const otherMetricSetup = setup?.otherMetric?.metricSetup;
		if (otherMetricSetup) {
			const normalizedMetricSetup = this.normalizeMetricSetupData(otherMetricSetup)!;
			attributes.otherMetricSetupKey = normalizedMetricSetup.key;
			attributes.otherMetricSetupSpaceAccessId = normalizedMetricSetup.spaceAccessId;
			attributes.otherMetricSetupDimensions = normalizedMetricSetup.dimensions;
			attributes.otherMetricSetupFilters = normalizedMetricSetup.filters || null;
		}

		// undefined values are skipped by ED
		attributes.historyValues = setup?.historyValues;
		attributes.primaryGranulation = setup?.primaryGranulation;
		attributes.endDate = setup?.endDate;
		attributes.formatType = setup?.formatType;
		attributes.targetValue = setup?.targetValue;
		attributes.targetValueType = setup?.targetValueType;
		attributes.startValue = setup?.startValue;
		attributes.formatUnit = setup?.formatUnit;
		attributes.timeframe = setup?.timeframe;
		attributes.targetSuccessCondition = setup?.targetSuccessCondition;
		attributes.decimals = setup?.decimals;

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(snapshot: Snapshot, options: SerializerOptions): RecordData.Goal.Serialized {
		const attributes = super.serialize(snapshot, options) as RecordAttributes<GoalRecord>;

		const setup: RecordData.Goal.Setup = {
			primaryGranulation: attributes.primaryGranulation,
			targetValue: attributes.targetValue,
			targetValueType: attributes.targetValueType,
			endDate: attributes.endDate,
			startValue: attributes.startValue ?? null,
			timeframe:
				attributes.timeframe && Object.keys(attributes.timeframe).length
					? attributes.timeframe
					: null,
			targetSuccessCondition: attributes.targetSuccessCondition,
			decimals: attributes.decimals,
		};
		if (attributes.mainMetricSetupKey) {
			setup.mainMetric = {
				metricSetup: this.serializeMetricSetupData({
					key: attributes.mainMetricSetupKey,
					spaceAccessId: attributes.mainMetricSetupSpaceAccessId,
					dimensions: attributes.mainMetricSetupDimensions,
					filters: attributes.mainMetricSetupFilters,
				})!,
			};
		}
		if (attributes.otherMetricSetupKey) {
			setup.otherMetric = {
				metricSetup: this.serializeMetricSetupData({
					key: attributes.otherMetricSetupKey,
					spaceAccessId: attributes.otherMetricSetupSpaceAccessId,
					dimensions: attributes.otherMetricSetupDimensions,
					filters: attributes.otherMetricSetupFilters,
				})!,
			};
		}
		if (attributes.historyValues) {
			setup.historyValues = attributes.historyValues;
		}
		if (attributes.formatType) {
			setup.formatType = attributes.formatType;
		}
		if (attributes.formatUnit) {
			setup.formatUnit = attributes.formatUnit;
		}

		return {
			...omit(
				attributes,
				// moved to setup.mainMetric / setup.otherMetric
				'mainMetricSetupKey',
				'mainMetricSetupSpaceAccessId',
				'mainMetricSetupDimensions',
				'mainMetricSetupFilters',
				'otherMetricSetupKey',
				'otherMetricSetupSpaceAccessId',
				'otherMetricSetupDimensions',
				'otherMetricSetupFilters',
				// moved to setup
				'historyValues',
				'primaryGranulation',
				'endDate',
				'formatType',
				'targetValue',
				'targetValueType',
				'startValue',
				'formatUnit',
				'timeframe',
				'targetSuccessCondition',
				'decimals',
				// not sent
				'created',
			),
			setup,
		};
	}
}
